import { useState } from 'react';

/**
 * The portal's "Your content calendar" window: Month, Week, Day and Agenda
 * views, shown on top of the sample portal. Sample items only.
 */

export type CalStatus = 'Queued' | 'Scheduled';
export interface CalItem { date: string; title: string; meta: string; status: CalStatus }
type Mode = 'Month' | 'Week' | 'Day' | 'Agenda';

const TODAY = new Date(2026, 9, 18);
const MODES: Mode[] = ['Month', 'Week', 'Day', 'Agenda'];
const DOW = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MON3 = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const DAY3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const key = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const mondayOf = (d: Date) => addDays(d, -((d.getDay() + 6) % 7));
const same = (a: Date, b: Date) => key(a) === key(b);

const Chev = ({ dir }: { dir: 'l' | 'r' }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={dir === 'l' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} /></svg>
);

export const HomeCalendar = ({ items, onClose, onInteract }: { items: CalItem[]; onClose: () => void; onInteract: () => void }) => {
  const [mode, setMode] = useState<Mode>('Day');
  const [cur, setCur] = useState<Date>(TODAY);
  const byDay = (d: Date) => items.filter((i) => i.date === key(d));

  const shift = (n: number) => {
    onInteract();
    if (mode === 'Month' || mode === 'Agenda') setCur(new Date(cur.getFullYear(), cur.getMonth() + n, 1));
    else if (mode === 'Week') setCur(addDays(cur, 7 * n));
    else setCur(addDays(cur, n));
  };

  const title =
    mode === 'Day' ? `${DAY3[cur.getDay()]}, ${MON3[cur.getMonth()][0]}${MON3[cur.getMonth()].slice(1).toLowerCase()} ${cur.getDate()}`
    : mode === 'Week' ? (() => { const m = mondayOf(cur); const e = addDays(m, 6); return `${MON3[m.getMonth()][0]}${MON3[m.getMonth()].slice(1).toLowerCase()} ${m.getDate()} – ${e.getDate()}`; })()
    : `${MONTHS[cur.getMonth()]} ${cur.getFullYear()}`;

  const monthCells = () => {
    const first = new Date(cur.getFullYear(), cur.getMonth(), 1);
    const start = mondayOf(first);
    const last = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
    const n = Math.ceil(((last.getTime() - start.getTime()) / 86400000 + 1) / 7) * 7;
    return Array.from({ length: n }, (_, i) => addDays(start, i));
  };

  return (
    <div className="hd-modal-bg" role="dialog" aria-modal="true" aria-label="Your content calendar" onClick={onClose}>
      <div className="hd-modal" onClick={(e) => { e.stopPropagation(); onInteract(); }}>
        <div className="hd-modal-h">
          <b>Your content calendar</b>
          <button type="button" className="hd-x" aria-label="Close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
        <div className="hd-modal-b">
          <div className="hd-cal-tools">
            <div className="hd-cal-nav">
              <button type="button" className="hd-sq" aria-label="Previous" onClick={() => shift(-1)}><Chev dir="l" /></button>
              <button type="button" className="hd-today" onClick={() => { onInteract(); setCur(TODAY); }}>Today</button>
              <button type="button" className="hd-sq" aria-label="Next" onClick={() => shift(1)}><Chev dir="r" /></button>
              <span className="hd-cal-title">{title}</span>
            </div>
            <div className="hd-seg" role="tablist">
              {MODES.map((m) => (
                <button key={m} type="button" role="tab" aria-selected={m === mode} className={m === mode ? 'on' : undefined} onClick={() => { onInteract(); setMode(m); }}>{m}</button>
              ))}
            </div>
          </div>

          {mode === 'Day' && (
            <div className="hd-cal-box">
              {byDay(cur).length === 0
                ? <p className="hd-empty">Nothing scheduled for this day.</p>
                : byDay(cur).map((i) => (<div key={i.title} className={`hd-wk-item ${i.status.toLowerCase()}`}><b>{i.title}</b><span>{i.meta}</span></div>))}
            </div>
          )}

          {mode === 'Week' && (
            <div className="hd-week-grid">
              {Array.from({ length: 7 }, (_, i) => addDays(mondayOf(cur), i)).map((d, i) => (
                <div key={i} className="hd-wk-col">
                  <p><span>{DOW[i]}</span> {same(d, TODAY) ? <i className="hd-now">{d.getDate()}</i> : <b>{d.getDate()}</b>}</p>
                  {byDay(d).length === 0 ? <em>&mdash;</em> : byDay(d).map((it) => (<div key={it.title} className={`hd-wk-item ${it.status.toLowerCase()}`}><b>{it.title}</b><span>{it.meta}</span></div>))}
                </div>
              ))}
            </div>
          )}

          {mode === 'Month' && (
            <div className="hd-cal-box hd-month">
              {DOW.map((d) => (<div key={d} className="hd-dow">{d}</div>))}
              {monthCells().map((d) => {
                const its = byDay(d);
                const out = d.getMonth() !== cur.getMonth();
                return (
                  <div key={key(d)} className={`hd-cell${out ? ' out' : ''}`}>
                    {same(d, TODAY) ? <i className="hd-now">{d.getDate()}</i> : <b>{d.getDate()}</b>}
                    {its.slice(0, 2).map((it) => (<span key={it.title} className={`hd-chip2 ${it.status.toLowerCase()}`}>{it.title}</span>))}
                    {its.length > 2 && <small>+{its.length - 2} more</small>}
                  </div>
                );
              })}
            </div>
          )}

          {mode === 'Agenda' && (
            <div className="hd-cal-box hd-agenda">
              {items.filter((i) => i.date.startsWith(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`)).sort((a, b) => a.date.localeCompare(b.date)).map((i) => {
                const [y, m, dd] = i.date.split('-').map(Number);
                return (
                  <div key={i.title} className="hd-ag-row">
                    <span className="hd-ag-date"><small>{MON3[m - 1]}</small><b>{dd}</b></span>
                    <span className="hd-ag-t"><b>{i.title}</b><span>{i.meta}</span></span>
                    <em className={`hd-st ${i.status.toLowerCase()}`}>{i.status}</em>
                    <span hidden>{y}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
